<script setup>

    import { computed, ref, watch } from 'vue'

    import { TrashIcon } from '@heroicons/vue/20/solid'
    import { IconClipboardLink20Filled } from '@iconify-prerendered/vue-fluent'

    import Modal from '@/Components/App/Modal.vue'
    import LatteClientContactViewList from '@/Components/Contacts/LatteClientContactViewList.vue'
    import LatteContactForm from '@/Components/Contacts/LatteContactForm.vue'
    import DangerButton from '@/Components/Forms/DangerButton.vue'
    import SecondaryButton from '@/Components/Forms/SecondaryButton.vue'

    import Loader from '@/Components/App/Loader.vue'
    import { useClientSingleStore, useFormStatusStore, useNotificationStore, useViewStore } from '@/Stores'
    import { storeToRefs } from 'pinia'
    import { useJobRunner } from '@/Helpers/jobRunner'
    import { CARE_RECIPIENT_CONTACT_TYPE, deepCopy } from '@/Helpers'
    import { finish, isFinished, isRunBefore, run } from '@/Helpers/jobState'

    const clientSingleStore = useClientSingleStore()
    const formStatusStore = useFormStatusStore()
    const viewStore = useViewStore()
    const { client } = storeToRefs(clientSingleStore)
    const { isEmbedded } = storeToRefs(viewStore)

    const emit = defineEmits(['update:selected', 'sms:triggered', 'update:clientData'])

    // When the component is embedded in Coffee, we need to adjust the vertical offset of the deletion modal (pixels)
    const coffeeEmbeddedVerticalOffset = -800

    const props = defineProps({
        createClient: {
            type: Boolean,
            default: false
        },
        addNew: {
            type: Boolean,
            default: false
        },
        selectedContactId: {
            type: Number,
            default: null,
        },
        showNotification: {
            type: Boolean,
            default: true
        },
        smsEnabled: {
            type: Boolean,
            default: true
        },
        enable: {
            type: Boolean,
            default: true
        }
    })

    let notificationStore = { add: () => {}, removeById: () => {}}
    watch(() => props.showNotification, function(newValue) {
        if (newValue == true) {
            notificationStore = useNotificationStore()
        }
    }, {immediate : true})

    const jobContactRunner = useJobRunner('contact')
    jobContactRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        clientSingleStore.stopInactivityTimeout()
    })
    jobContactRunner.subscribe('finishedJobSuccess', function() {
        clientSingleStore.resetInactivityRefreshTimer()
    })
    jobContactRunner.subscribe('addedJob', function() {
        clientSingleStore.resetInactivityRefreshTimer()
        notificationStore.removeById(clientSingleStore.getMaxRetryDialogId())
        clientSingleStore.removeMaxRetryDialogId()
    })
    jobContactRunner.subscribe('reachedMaxRetry', function() {
        const maxRetryDialogId = notificationStore.add('Error Saving Changes', 'error', 'Make an edit to try again.', 7 * 24 * 60 * 60 * 1000)
        clientSingleStore.setMaxRetryDialogId(maxRetryDialogId)
    })

    const jobClientRunner = useJobRunner('client')
    jobClientRunner.subscribe('addedRetryJob', function(errorCode, retryTime) {
        notificationStore.add('Error Saving Changes', 'error', `Retrying automatically... (${retryTime}/20)`, 15000)
        clientSingleStore.stopInactivityTimeout()
    })
    jobClientRunner.subscribe('finishedJobSuccess', function() {
        clientSingleStore.resetInactivityRefreshTimer()
    })
    jobClientRunner.subscribe('addedJob', function() {
        clientSingleStore.resetInactivityRefreshTimer()
    })

    const confirmingContactDeletion = ref(false)
    const pendingDelete = ref(false)
    const loading = computed(() => {
        if (client.value.fe_generated) {
            return !props.addNew
        }
        return false
    })

    // A client with no contacts has nothing to select, so the blank form is the only useful view --
    // show it without waiting for the user to click Add New
    const isAddNewMode = computed(() => props.addNew || (client.value.contacts?.length ?? 0) === 0)

    const defaultContact = {
        id: null,
        primary_contact: false,
        billing_contact: false,
        marketable_contact: {},
        comm_phone: false,
        comm_sms: false,
        comm_email: false,
        type: null,
        first_name: "",
        last_name: "",
        address: "",
        city: "",
        state: null,
        zip: "",
        profession: null,
        gender: "",
        company: "",
        note: "",
        birthday: null,
        emails: [],
        phones: [
            {
                number: "",
                country_code: "1",
                type: "cell"
            },
            {
                number: "",
                country_code: "1",
                type: "work"
            },
            {
                number: "",
                country_code: "1",
                type: "home"
            }
        ]
    }

    async function hydrateContact(data) {
        await clientSingleStore.hydrateContact(data)
    }

    const selectedContact = ref({...defaultContact})

    // Keep selectedContact in sync when the client contacts array updates (e.g., after hydrateContact)
    watch(() => client.value.contacts, (contacts) => {
        try {
            const currentId = selectedContact.value?.id
            if (!currentId) return
            const index = (contacts || []).findIndex(item => item.id == currentId)
            if (index !== -1) {
                selectedContact.value = contacts[index]
            }
        } catch (e) {
            // ignore
        }
    }, { deep: true })

    function selectContact(contact_id) {
        const index = client.value.contacts.findIndex(item => item.id == contact_id);
        if (index != -1) {
            selectedContact.value = client.value.contacts[index]

            if (selectedContact.value.type === CARE_RECIPIENT_CONTACT_TYPE) {
                history.replaceState(null, '', window.location.pathname + window.location.search)
            } else if (contact_id) {
                history.replaceState(null, '', `#contact-${contact_id}`)
            }

            emit('update:selected', contact_id)
        }
    }

    function isContactStillSelected(contact, savedContactId) {
        return contact?.id != null && contact.id == savedContactId
    }

    // A save that resolves after the user moved on must refresh the contact list data only,
    // never pull the view back to the contact that was saved
    function refreshSelectedContactIfStillShown(contactId) {
        if (!isContactStillSelected(selectedContact.value, contactId)) {
            return
        }

        const index = client.value.contacts.findIndex(item => item.id == contactId)
        if (index !== -1) {
            selectedContact.value = client.value.contacts[index]
        }
    }

    function unMarkTypeClientContact(excludeId) {
        client.value.contacts.forEach(contact => {
            if (contact.type === CARE_RECIPIENT_CONTACT_TYPE && contact.id != excludeId) {
                contact.type = null
                return;
            }
        })
    }

    function formatName(contact) {
        if (contact.first_name && contact.last_name) {
            return contact.first_name + ' ' + contact.last_name;
        } else if (contact.first_name) {
            return contact.first_name;
        } else if (contact.last_name) {
            return contact.last_name;
        } else if (contact.company) {
            return contact.company;
        } else {
            return 'Unnamed Contact ' + contact.id;
        }
    }

    function getSaveSuccessMessage(contact) {
        return 'Successfully saved contact: ' + formatName(contact);
    }

    function getDeleteSuccessMessage(contact) {
        return 'Successfully deleted contact: ' + formatName(contact);
    }

    function getSaveErrorMessage(contact) {
        return 'Error saving contact: ' + formatName(contact);
    }

    function getDeleteErrorMessage(contact) {
        return 'Error deleting contact: ' + formatName(contact);
    }

    function getModifiedFields(oldContact, newContact) {
        var modifiedFields = []
        for (var field in oldContact) {
            if (field === 'address_validity') {
                continue
            }

            if (oldContact[field] != newContact[field]) {
                if (field == 'emails') {
                    let result = oldContact[field].filter(email => !newContact[field].includes(email));
                    if (result.length > 0) {
                        modifiedFields.push(field)
                    }
                } else if (field == 'phones') {
                    let result = oldContact[field].filter(oldPhoneObject =>
                        !newContact[field].some(newPhoneObject => {
                            return oldPhoneObject.number === newPhoneObject.number &&
                                oldPhoneObject.type === newPhoneObject.type &&
                                oldPhoneObject.country_code === newPhoneObject.country_code;
                        })
                    )
                    if (result.length > 0) {
                        modifiedFields.push(field)
                    }
                } else {
                    modifiedFields.push(field)
                }
            }
        }
        return modifiedFields;
    }


    let contactFollowUpDataAfterCreate = null
    let originalContactBeforePostMessage = {}
    function addUpdateContactJob(contact, formId, runNow = false) {
        const func = runNow ? 'addAndRunJob' : 'addJob'

        jobContactRunner[func]('update', contact.id, contact, [formId], async (contact, formId, jobId) => {
            const index = client.value.contacts.findIndex(item => item.id == contact.id)
            if (!originalContactBeforePostMessage[contact.id]) {
                originalContactBeforePostMessage[contact.id] = deepCopy(client.value.contacts[index])
            }

            const oldContactType = client.value.contacts[index].type
            client.value.contacts[index] = deepCopy(contact)
            refreshSelectedContactIfStillShown(contact.id)

            if (contact.type !== CARE_RECIPIENT_CONTACT_TYPE && oldContactType === CARE_RECIPIENT_CONTACT_TYPE) {
                unMarkTypeClientContact()
            }

            if (contact.type == CARE_RECIPIENT_CONTACT_TYPE) {
                unMarkTypeClientContact(contact.id)
            }

            await clientSingleStore.updateContact(client.value, contact, formId)
                .then(function (response) {
                    if (jobContactRunner.hasScheduleJob('update', contact.id) == false) {
                        hydrateContact(response.data)
                        refreshSelectedContactIfStillShown(contact.id)
                    }

                    formStatusStore.add('contactForm', getSaveSuccessMessage(response.data))

                    jobContactRunner.handleJobSuccess('update', contact.id, jobId)

                    const modifiedFields = getModifiedFields(originalContactBeforePostMessage[contact.id], contact)
                    window.parent.postMessage({
                        'clientUpdate': response.data.type === CARE_RECIPIENT_CONTACT_TYPE || oldContactType === CARE_RECIPIENT_CONTACT_TYPE,
                        'modifiedFields': modifiedFields,
                    }, '*')
                    delete originalContactBeforePostMessage[contact.id]

                    emit('update:clientData')
                    notificationStore.add('Changes Saved')
                })
                .catch(function(error) {
                    formStatusStore.add('contactForm', getSaveErrorMessage(contact), 'error')

                    jobContactRunner.handleJobError('update', contact.id, jobId, error.error.response?.status)
                })
        })
    }

    async function updateContact(contact, formId) {
        if (contact.id) {
            // Guard: Prevent update if client data hasn't loaded yet so no update is attempted
            if (!client.value.id || client.value.fe_generated) {
                return
            }
            addUpdateContactJob(contact, formId)
        } else {
            // Guard: Prevent update if client data hasn't loaded yet so no new client is created
            if (!client.value.id && !props.addNew) {
                return
            }

            if (isRunBefore('contact', 'create') == false || isFinished('contact', 'create') == true) {
                jobContactRunner.addJob('create', null, contact, [formId], async (contact, formId, jobId) => {
                    run('contact', 'create')
                    await clientSingleStore.storeContactUsingClientUuid(client.value.uuid, contact, formId)
                        .then(function (response) {
                            if (response.data.type === CARE_RECIPIENT_CONTACT_TYPE) {
                                unMarkTypeClientContact()
                            }

                            clientSingleStore.hydrateContact(response.data)

                            if (selectedContact.value.id == null) {
                                selectContact(response.data.id)
                            }

                            formStatusStore.add('contactForm', getSaveSuccessMessage(response.data))
                            window.parent.postMessage({
                                'clientUpdate': response.data.type === CARE_RECIPIENT_CONTACT_TYPE,
                                'modifiedFields': Object.keys(response.data),
                            }, '*')

                            jobContactRunner.handleJobSuccess('create', null, jobId)
                            finish('contact', 'create')
                            if (contactFollowUpDataAfterCreate) {
                                addUpdateContactJob({...contactFollowUpDataAfterCreate, id: response.data.id}, formId, true)
                            }
                            emit('update:clientData')
                            notificationStore.add('Changes Saved')
                        })
                        .catch(function(error) {
                            finish('contact', 'create')
                            formStatusStore.add('contactForm', "Failed to create new contact", 'error')
                            if (contactFollowUpDataAfterCreate) {
                                updateContact(contactFollowUpDataAfterCreate)
                            }
                            jobContactRunner.handleJobError('create', null, jobId, error.error.response?.status)
                        })
                        .finally(function() {
                            contactFollowUpDataAfterCreate = null
                        })
                })
            } else {
                contactFollowUpDataAfterCreate = contact
            }
        }
    }

    function deleteContact(contact) {
        pendingDelete.value = true
        clientSingleStore.deleteContact(client.value, contact)
            .then(function (response) {
                if (response.status == 204) {
                    const index = client.value.contacts.findIndex(item => item.id == contact.id)
                    formStatusStore.add('contactForm', getDeleteSuccessMessage(contact))
                    client.value.contacts.splice(index, 1)
                    selectedContact.value = client.value.contacts.length == 0 ? {...defaultContact} : client.value.contacts[0]
                    pendingDelete.value = false
                    closeModal()
                }
            }).catch(function() {
                formStatusStore.add('contactForm', getDeleteErrorMessage(contact), 'error')
                pendingDelete.value = false
            })
    }

    const confirmContactDeletion = () => {
        confirmingContactDeletion.value = true;
    }

    const closeModal = () => {
        confirmingContactDeletion.value = false;
    }

    async function copyContactLink() {
        if (!selectedContact.value.id) return

        const baseUrl = window.location.origin + window.location.pathname
        const contactUrl = `${baseUrl}#contact-${selectedContact.value.id}`

        try {
            await navigator.clipboard.writeText(contactUrl)
            notificationStore.add('Contact Link Copied to Clipboard')
        } catch (err) {
            notificationStore.add('Failed to Copy Link', 'error')
        }
    }

    const initialContactSelectionApplied = ref(false)

    watch(() => [isAddNewMode.value, loading.value], (value) => {
        if (isAddNewMode.value && !loading.value) {
            selectedContact.value = {...defaultContact}
            if (client.value.contacts.length == 0) {
                selectedContact.value.type = CARE_RECIPIENT_CONTACT_TYPE
            }

            if (clientSingleStore.isPrimaryContactAddNewEnable()) {
                selectedContact.value.primary_contact = true
                clientSingleStore.changeAddNewPrimaryContactMode(false)
            }
        }
    }, {immediate: true, deep: true})

    watch(() => client.value, (value) => {
        if (!props.addNew) {
            selectContact(selectedContact.value.id)
        }

        if (props.selectedContactId && value.contacts?.length > 0 && !initialContactSelectionApplied.value) {
            const contactExists = value.contacts.some(c => c.id === props.selectedContactId)
            if (contactExists && selectedContact.value.id !== props.selectedContactId) {
                selectContact(props.selectedContactId)
                initialContactSelectionApplied.value = true
            }
        }
    })

    watch(
        [() => props.selectedContactId, () => client.value.contacts],
        ([contactId, contacts]) => {
            if (contactId && contacts?.length > 0 && !initialContactSelectionApplied.value) {
                selectContact(contactId)
                initialContactSelectionApplied.value = true
            }
        },
        { immediate: true }
    )

</script>

<template>

    <div
        v-if="loading || props.enable == false"
        class="relative z-50 h-[46rem] -mb-[46rem] flex justify-center items-center bg-lifeworx-blue-800 bg-opacity-50"
    >
        <Loader />
    </div>

    <div class="grid grid-cols-4 md:grid-cols-16 h-[46rem]">

        <div class="col-span-4 md:col-span-5 md:overflow-scroll md:border-r-2">
            <LatteClientContactViewList
                :contacts="client.contacts"
                @update:selected="selectContact"
                :selected="selectedContact"
                :addNew="(!loading && isAddNewMode)"
            />
        </div>

        <div class="hidden md:block md:col-span-11">
            <LatteContactForm
                v-if="(!loading && isAddNewMode) || selectedContact.id"
                @update="updateContact"
                @sms:triggered="$emit('sms:triggered', $event)"
                :contact="selectedContact"
                :smsEnabled="props.smsEnabled"
            >

                <template #actions>
                    <button
                        v-if="selectedContact.id"
                        @click.stop="copyContactLink"
                        class="-mt-1 p-1 grid justify-center group/link text-lifeworx-blue-600 hover:text-lifeworx-blue-800"
                        title="Copy contact link"
                    >
                        <IconClipboardLink20Filled class="w-6 h-6" />
                    </button>
                    <button
                        :disabled="selectedContact.type === CARE_RECIPIENT_CONTACT_TYPE"
                        v-if="client.contacts.length > 0"
                        @click.stop="confirmContactDeletion"
                        class="-mt-1 p-1 grid justify-center group/trash"
                        :class="{
                            'text-lifeworx-red-500 hover:text-lifeworx-red-800': selectedContact.type !== CARE_RECIPIENT_CONTACT_TYPE,
                            'text-gray-400/50 cursor-default': selectedContact.type === CARE_RECIPIENT_CONTACT_TYPE,
                        }"
                        title="Delete contact"
                    >
                        <TrashIcon class="w-6 h-6" />
                    </button>
                </template>

            </LatteContactForm>

        </div>

    </div>

    <Modal
        :show="confirmingContactDeletion"
        :verticalOffset="isEmbedded ? coffeeEmbeddedVerticalOffset : null"
    >
        <div class="p-6">
            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">
                Are you sure you want to delete this contact?
            </h2>

            <div class="mt-6 flex justify-end">
                <SecondaryButton
                    :disabled="pendingDelete"
                    @click="closeModal"
                >
                    Cancel
                </SecondaryButton>

                <DangerButton
                    class="ml-3"
                    :class="{ 'opacity-50': pendingDelete }"
                    :disabled="pendingDelete"
                    @click="deleteContact(selectedContact)"
                >
                    {{ !pendingDelete ? 'Delete Contact' : 'Deleting... ' }}
                    <svg class="-mr-2 ml-2 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" v-if="pendingDelete">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>

                </DangerButton>
            </div>
        </div>
    </Modal>

</template>
